import { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Layout,
  Text,
  Button,
  Badge,
  ProgressBar,
  Checkbox,
  TextField,
  Banner,
  Spinner,
  BlockStack,
  InlineStack,
  Divider,
  List,
} from '@shopify/polaris';
import {
  PlusMinor,
  DeleteMinor
} from '@shopify/polaris-icons';
import axios from 'axios';
import toast from 'react-hot-toast';

interface VerificationStatus {
  id: string;
  isVerified: boolean;
  verificationStatus: 'NOT_APPLIED' | 'IN_PROGRESS' | 'APPROVED' | 'REJECTED' | 'REVOKED';
  verificationAppliedAt?: string;
  verificationApprovedAt?: string;
  verificationRejectedAt?: string;
  verificationRejectionReason?: string;
  verificationChecklist: any;
  verificationMediaLinks: any[];
}

interface ReadinessData {
  isReady: boolean;
  readinessPercentage: number;
  totalScore: number;
  maxScore: number;
  requirements: any;
  unmetRequirements: string[];
  recommendation: string;
  nextSteps: string[];
}

interface MediaLink {
  url: string;
  title: string;
  publication: string;
  publishedDate: string;
}

export default function VerificationGuidePage() {
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus | null>(null);
  const [readinessData, setReadinessData] = useState<ReadinessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'guide' | 'checklist' | 'media' | 'status'>('status');

  // Checklist state
  const [checklist, setChecklist] = useState({
    businessManagerVerified: false,
    twoFactorEnabled: false,
    displayNameApproved: false,
    qualityRatingGreen: false,
    tierOneOrHigher: false
  });

  // Media links state
  const [mediaLinks, setMediaLinks] = useState<MediaLink[]>([]);
  const [newMediaLink, setNewMediaLink] = useState<MediaLink>({
    url: '',
    title: '',
    publication: '',
    publishedDate: ''
  });

  const storeId = localStorage.getItem('storeId') || '';
  const token = localStorage.getItem('token') || '';

  useEffect(() => {
    fetchVerificationStatus();
    fetchReadinessData();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/verification/status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = response.data.data;
      setVerificationStatus(data);

      // Initialize checklist from API
      if (data.verificationChecklist && typeof data.verificationChecklist === 'object') {
        setChecklist({
          businessManagerVerified: data.verificationChecklist.businessManagerVerified || false,
          twoFactorEnabled: data.verificationChecklist.twoFactorEnabled || false,
          displayNameApproved: data.verificationChecklist.displayNameApproved || false,
          qualityRatingGreen: data.verificationChecklist.qualityRatingGreen || false,
          tierOneOrHigher: data.verificationChecklist.tierOneOrHigher || false
        });
      }

      // Initialize media links from API
      if (Array.isArray(data.verificationMediaLinks)) {
        setMediaLinks(data.verificationMediaLinks);
      }
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
      toast.error('Failed to load verification status');
    } finally {
      setLoading(false);
    }
  };

  const fetchReadinessData = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/verification/readiness`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setReadinessData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch readiness data:', error);
    }
  };

  const updateChecklist = async (updatedChecklist: any) => {
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/verification/checklist`,
        { checklist: updatedChecklist },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Checklist updated');
      fetchReadinessData();
    } catch (error) {
      console.error('Failed to update checklist:', error);
      toast.error('Failed to update checklist');
    }
  };

  const updateMediaLinks = async (links: MediaLink[]) => {
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/verification/media-links`,
        { mediaLinks: links },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Media links updated');
      fetchReadinessData();
    } catch (error) {
      console.error('Failed to update media links:', error);
      toast.error('Failed to update media links');
    }
  };

  const handleChecklistChange = (field: keyof typeof checklist, value: boolean) => {
    const updatedChecklist = { ...checklist, [field]: value };
    setChecklist(updatedChecklist);
    updateChecklist(updatedChecklist);
  };

  const handleAddMediaLink = () => {
    if (!newMediaLink.url || !newMediaLink.title || !newMediaLink.publication) {
      toast.error('Please fill in all required fields');
      return;
    }

    const updatedLinks = [...mediaLinks, newMediaLink];
    setMediaLinks(updatedLinks);
    updateMediaLinks(updatedLinks);

    // Reset form
    setNewMediaLink({
      url: '',
      title: '',
      publication: '',
      publishedDate: ''
    });
  };

  const handleRemoveMediaLink = (index: number) => {
    const updatedLinks = mediaLinks.filter((_, i) => i !== index);
    setMediaLinks(updatedLinks);
    updateMediaLinks(updatedLinks);
  };

  const handleMarkAsApplied = async () => {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/verification/apply`,
        { mediaLinks },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Verification application recorded');
      fetchVerificationStatus();
      fetchReadinessData();
    } catch (error) {
      console.error('Failed to record verification application:', error);
      toast.error('Failed to record verification application');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'NOT_APPLIED':
        return <Badge>Not Applied</Badge>;
      case 'IN_PROGRESS':
        return <Badge tone="info">In Progress</Badge>;
      case 'APPROVED':
        return <Badge tone="success">Approved</Badge>;
      case 'REJECTED':
        return <Badge tone="critical">Rejected</Badge>;
      case 'REVOKED':
        return <Badge tone="warning">Revoked</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  if (loading) {
    return (
      <Page title="WhatsApp Business Verification">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const renderStatusSection = () => (
    <Layout.Section>
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="center" align="space-between">
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">
                  Current Status
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  Track your WhatsApp Business verification progress
                </Text>
              </BlockStack>
              {verificationStatus && getStatusBadge(verificationStatus.verificationStatus)}
            </InlineStack>

            <Divider />

            {verificationStatus?.isVerified && (
              <Banner tone="success" title="Congratulations!">
                Your WhatsApp Business account is verified. The blue checkmark is now visible next to your business name.
              </Banner>
            )}

            {verificationStatus?.verificationStatus === 'REJECTED' && (
              <Banner tone="critical" title="Application Rejected">
                <p>
                  <strong>Reason:</strong> {verificationStatus.verificationRejectionReason || 'No reason provided'}
                </p>
                <p style={{ marginTop: '8px' }}>
                  You can reapply after 30 days. Review the requirements and improve your application.
                </p>
              </Banner>
            )}

            {verificationStatus?.verificationStatus === 'IN_PROGRESS' && (
              <Banner tone="info" title="Application Under Review">
                <p>Your verification application was submitted on {new Date(verificationStatus.verificationAppliedAt!).toLocaleDateString()}</p>
                <p style={{ marginTop: '8px' }}>
                  Meta typically reviews applications within 1-2 business days. You will be notified of the decision.
                </p>
              </Banner>
            )}

            {readinessData && (
              <>
                <BlockStack gap="200">
                  <InlineStack gap="400" blockAlign="center" align="space-between">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      Readiness Score
                    </Text>
                    <Text as="p" variant="bodyMd" fontWeight="bold">
                      {readinessData.readinessPercentage}%
                    </Text>
                  </InlineStack>
                  <ProgressBar progress={readinessData.readinessPercentage} size="small" />
                </BlockStack>

                <Banner
                  tone={readinessData.isReady ? 'success' : readinessData.readinessPercentage >= 50 ? 'warning' : 'info'}
                >
                  {readinessData.recommendation}
                </Banner>

                {readinessData.unmetRequirements.length > 0 && (
                  <BlockStack gap="200">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      Next Steps:
                    </Text>
                    <List>
                      {readinessData.nextSteps.map((step, index) => (
                        <List.Item key={index}>{step}</List.Item>
                      ))}
                    </List>
                  </BlockStack>
                )}
              </>
            )}
          </BlockStack>
        </Card>
      </Layout.Section>
    );

  const renderChecklistSection = () => (
    <Layout.Section>
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="200">
            <Text as="h2" variant="headingMd">
              Verification Checklist
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              Complete these requirements before applying
            </Text>
          </BlockStack>

          <Divider />

          <BlockStack gap="400">
              <Checkbox
                label="Meta Business Manager Verified"
                checked={checklist.businessManagerVerified}
                onChange={(value) => handleChecklistChange('businessManagerVerified', value)}
                helpText="Complete KYC verification in Meta Business Manager"
              />

              <Checkbox
                label="Two-Factor Authentication Enabled"
                checked={checklist.twoFactorEnabled}
                onChange={(value) => handleChecklistChange('twoFactorEnabled', value)}
                helpText="Enable 2FA on all admin accounts in Business Manager"
              />

              <Checkbox
                label="Display Name Approved"
                checked={checklist.displayNameApproved}
                onChange={(value) => handleChecklistChange('displayNameApproved', value)}
                helpText="WhatsApp display name must be approved by Meta"
              />

              <Checkbox
                label="Quality Rating is Green"
                checked={checklist.qualityRatingGreen}
                onChange={(value) => handleChecklistChange('qualityRatingGreen', value)}
                helpText="Maintain a good (green) quality rating"
              />

              <Checkbox
                label="Messaging Tier 1 or Higher"
                checked={checklist.tierOneOrHigher}
                onChange={(value) => handleChecklistChange('tierOneOrHigher', value)}
                helpText="Reach Tier 1 or higher messaging limits (recommended)"
              />
          </BlockStack>
        </BlockStack>
      </Card>
    </Layout.Section>
  );

  const renderMediaLinksSection = () => (
    <Layout.Section>
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="200">
            <Text as="h2" variant="headingMd">
              Media Coverage Links
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              Add 3-5 links from reputable news publications
            </Text>
          </BlockStack>

          <Banner tone="info">
              <p><strong>Requirements:</strong></p>
              <List>
                <List.Item>3-5 articles from reputable news publications</List.Item>
                <List.Item>Your business name must be featured prominently</List.Item>
                <List.Item>No paid advertisements or sponsored content</List.Item>
                <List.Item>Articles should be publicly accessible (no paywalls)</List.Item>
              </List>
            </Banner>

            <Divider />

          {mediaLinks.length > 0 && (
              <BlockStack gap="400">
                {mediaLinks.map((link, index) => (
                  <Card key={index}>
                    <BlockStack gap="400">
                      <InlineStack gap="400" blockAlign="center" align="space-between">
                        <BlockStack gap="200">
                          <Text as="p" variant="bodyMd" fontWeight="semibold">
                            {link.title}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            {link.publication}
                            {link.publishedDate && ` • ${link.publishedDate}`}
                          </Text>
                          <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: '#006fbb', textDecoration: 'none' }}>
                            {link.url}
                          </a>
                        </BlockStack>
                        <Button
                          icon={DeleteMinor}
                          onClick={() => handleRemoveMediaLink(index)}
                          tone="critical"
                          size="slim"
                        />
                      </InlineStack>
                    </BlockStack>
                  </Card>
                ))}
              </BlockStack>
          )}

          <Card>
              <BlockStack gap="400">
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                    Add Media Coverage
                  </Text>

                  <TextField
                    label="Article URL"
                    value={newMediaLink.url}
                    onChange={(value) => setNewMediaLink({ ...newMediaLink, url: value })}
                    placeholder="https://example.com/article"
                    autoComplete="off"
                  />

                  <TextField
                    label="Article Title"
                    value={newMediaLink.title}
                    onChange={(value) => setNewMediaLink({ ...newMediaLink, title: value })}
                    placeholder="Article headline"
                    autoComplete="off"
                  />

                  <TextField
                    label="Publication Name"
                    value={newMediaLink.publication}
                    onChange={(value) => setNewMediaLink({ ...newMediaLink, publication: value })}
                    placeholder="TechCrunch, Forbes, etc."
                    autoComplete="off"
                  />

                  <TextField
                    label="Published Date (Optional)"
                    value={newMediaLink.publishedDate}
                    onChange={(value) => setNewMediaLink({ ...newMediaLink, publishedDate: value })}
                    placeholder="January 2025"
                    autoComplete="off"
                  />

                  <Button
                    icon={PlusMinor}
                    onClick={handleAddMediaLink}
                    fullWidth
                  >
                    Add Media Link
                  </Button>
              </BlockStack>
          </Card>

          {readinessData?.isReady && verificationStatus?.verificationStatus === 'NOT_APPLIED' && (
            <Button
              variant="primary"
              size="large"
              onClick={handleMarkAsApplied}
              fullWidth
            >
              Mark as Applied (Submit via Meta Business Suite)
            </Button>
          )}
        </BlockStack>
      </Card>
    </Layout.Section>
  );

  const renderGuideSection = () => (
    <Layout.Section>
      <Card>
        <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              What is WhatsApp Business Verification?
            </Text>
            <Text as="p" variant="bodyMd">
              WhatsApp Business Verification (Blue Checkmark) is an official badge from Meta that confirms your business account is authentic and represents a notable, legitimate entity. This badge appears next to your business name in WhatsApp, building trust with customers.
            </Text>

            <Divider />

            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Benefits of Verification
              </Text>
              <List>
                <List.Item>Customers know they're talking to the real business</List.Item>
                <List.Item>Reduces suspicion of scams or impersonation</List.Item>
                <List.Item>Professional appearance and brand recognition</List.Item>
                <List.Item>Official recognition from Meta</List.Item>
              </List>
            </BlockStack>

            <Banner tone="warning">
              <p><strong>Important:</strong> Verification does NOT give you:</p>
              <List>
                <List.Item>Additional API features</List.Item>
                <List.Item>Priority support</List.Item>
                <List.Item>Higher message limits</List.Item>
              </List>
            </Banner>

            <Divider />

            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Eligibility Requirements
              </Text>

              <BlockStack gap="200">
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  1. Notable Business Status
                </Text>
                <Text as="p" variant="bodyMd">
                  Your business must be well-known and frequently searched in your industry or region. Established business with public recognition and substantial online presence.
                </Text>

                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  2. Media Coverage (CRITICAL)
                </Text>
                <Text as="p" variant="bodyMd">
                  3-5 articles from reputable news publications featuring your business name. Must be independent editorial coverage, not paid advertisements or sponsored content.
                </Text>

                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  3. Meta Business Manager
                </Text>
                <Text as="p" variant="bodyMd">
                  Verified Facebook Business Manager account with completed KYC process and two-factor authentication enabled.
                </Text>

                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  4. WhatsApp Business API Account
                </Text>
                <Text as="p" variant="bodyMd">
                  Must use WhatsApp Business API (Cloud API or On-Premises). Display Name must be approved by Meta. Phone number must be verified.
                </Text>
              </BlockStack>
            </BlockStack>

            <Divider />

            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Application Process
              </Text>
              <List type="number">
                <List.Item>Complete the verification checklist above</List.Item>
                <List.Item>Collect 3-5 media coverage links from reputable sources</List.Item>
                <List.Item>Go to Meta Business Suite (business.facebook.com)</List.Item>
                <List.Item>Select your WhatsApp Business Account</List.Item>
                <List.Item>Navigate to Account SettingsRequest Verification</List.Item>
                <List.Item>Submit your application with media coverage links</List.Item>
                <List.Item>Wait 1-2 business days for Meta's decision</List.Item>
              </List>
            </BlockStack>

            <Banner tone="info">
              <p><strong>Review Timeline:</strong></p>
              <p>Meta typically reviews applications within 1-2 business days, though it can take up to 7 days in rare cases.</p>
            </Banner>

            <Divider />

            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Common Rejection Reasons
              </Text>
              <List>
                <List.Item><strong>Insufficient Media Coverage:</strong> Less than 3 sources or sources not reputable enough</List.Item>
                <List.Item><strong>Display Name Mismatch:</strong> Name in articles doesn't match WhatsApp display name</List.Item>
                <List.Item><strong>Not Notable Enough:</strong> Business not well-known in its market</List.Item>
                <List.Item><strong>Paid/Promotional Content:</strong> Submitted links are advertisements or sponsored</List.Item>
                <List.Item><strong>New Business/Account:</strong> Business or WhatsApp account too new</List.Item>
                <List.Item><strong>Quality Rating Issues:</strong> Low quality rating or recent violations</List.Item>
              </List>
            </BlockStack>

            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Resources
              </Text>
              <BlockStack gap="200">
                <a href="https://business.facebook.com/business/help" target="_blank" rel="noopener noreferrer" style={{ color: '#006fbb', textDecoration: 'none' }}>
                  Meta Business Help Center ↗
                </a>
                <a href="https://developers.facebook.com/docs/whatsapp" target="_blank" rel="noopener noreferrer" style={{ color: '#006fbb', textDecoration: 'none' }}>
                  WhatsApp Business API Documentation ↗
                </a>
                <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" style={{ color: '#006fbb', textDecoration: 'none' }}>
                  Meta Business Manager ↗
                </a>
              </BlockStack>
            </BlockStack>
          </BlockStack>
        </Card>
    </Layout.Section>
  );

  return (
    <Page
      title="WhatsApp Business Verification"
      subtitle="Get the blue checkmark to verify your business"
    >
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="200">
              <InlineStack gap="200" align="start">
                <Button
                  variant={activeSection === 'status' ? 'primary' : 'secondary'}
                  onClick={() => setActiveSection('status')}
                >
                  Status & Readiness
                </Button>
                <Button
                  variant={activeSection === 'checklist' ? 'primary' : 'secondary'}
                  onClick={() => setActiveSection('checklist')}
                >
                  Checklist
                </Button>
                <Button
                  variant={activeSection === 'media' ? 'primary' : 'secondary'}
                  onClick={() => setActiveSection('media')}
                >
                  Media Coverage
                </Button>
                <Button
                  variant={activeSection === 'guide' ? 'primary' : 'secondary'}
                  onClick={() => setActiveSection('guide')}
                >
                  Guide
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {activeSection === 'status' && renderStatusSection()}
        {activeSection === 'checklist' && renderChecklistSection()}
        {activeSection === 'media' && renderMediaLinksSection()}
        {activeSection === 'guide' && renderGuideSection()}
      </Layout>
    </Page>
  );
}
